import {
    Button,
    ErrorBoundary as OriginalErrorBoundary,
    Typography
} from "matsuri-ui"
import { css } from "@emotion/react"

import React from "react"

interface ErrorBoundaryProps {
    children: React.ReactNode
    fallback404?: React.ReactNode
    fallback401?:
        React.ReactNode | ((props: { message: string }) => React.ReactNode)
    fallbackDefault?:
        | React.ReactNode
        | ((props: {
              error: Error
              errorInfo: React.ErrorInfo
          }) => React.ReactNode)
}

export const ErrorTemplate = ({
    title,
    children
}: {
    title: string
    children?: React.ReactNode
}) => {
    return (
        <div
            css={css`
                display: grid;
                gap: 24px;
                max-width: 1024px;
                padding: 48px 5vw 0px;
                margin: auto;
            `}
        >
            <header>
                <Typography variant="h1">{title}</Typography>
            </header>

            {children}
            <div>
                <Button
                    css={css`
                        float: right;
                    `}
                    variant="filled"
                    color="primary"
                    onClick={() => {
                        window.history.back()
                    }}
                >
                    前のページに戻る
                </Button>
            </div>
        </div>
    )
}

const NotFound = () => {
    return (
        <ErrorTemplate title="404 Not Found">
            <Typography>ページは見つかりませんでした。</Typography>
        </ErrorTemplate>
    )
}

const Unauthorized = ({ message }: { message: string }) => {
    return (
        <ErrorTemplate title="401 Unauthorized">
            <Typography>このページへのアクセス権限がありません。</Typography>
            <Typography>{message}</Typography>
        </ErrorTemplate>
    )
}

const errorToMessage = (error: Error, errorInfo: React.ErrorInfo) => {
    return JSON.stringify({
        name: error?.name,
        message: error?.message,
        componentStack: errorInfo?.componentStack,
        stack: error?.stack,
        cause: error?.cause
    })
}

interface DefaultErrorProps {
    error: Error
    errorInfo: React.ErrorInfo
}

const DefaultError = (props: DefaultErrorProps) => {
    const message = errorToMessage(props.error, props.errorInfo)

    return (
        <ErrorTemplate title="エラーが発生しました">
            {/* TODO: FeedBackButtonを復活させる */}
            <div>
                <Typography
                    css={css`
                        margin-bottom: 8px;
                    `}
                >
                    以下のデータをコピーして開発者に発生状況などと共に送付してください。
                </Typography>
                <textarea
                    css={css`
                        width: 100%;
                        min-height: 200px;
                        border: 1px solid black;
                    `}
                    value={message}
                    readOnly
                />
            </div>
        </ErrorTemplate>
    )
}

export const ErrorBoundary = ({
    children,
    fallback404,
    fallback401,
    fallbackDefault
}: ErrorBoundaryProps) => {
    return (
        <OriginalErrorBoundary
            fallback={({ error, errorInfo }) => {
                /**
                 * throw new Error(`${response.status}: ${response.statusText}`}`)
                 * のような形式でエラーが投げられる想定
                 */
                const code = error?.message.split(":")[0]
                const message = error?.message.split(":")[1]

                if (code === "404") {
                    return <>{fallback404 ?? <NotFound />}</>
                }

                if (code === "401") {
                    if (fallback401) {
                        return typeof fallback401 === "function" ? (
                            <>{fallback401({ message })}</>
                        ) : (
                            <>{fallback401}</>
                        )
                    }
                    return <Unauthorized message={message} />
                }

                if (fallbackDefault) {
                    return typeof fallbackDefault === "function" ? (
                        <>{fallbackDefault({ error, errorInfo })}</>
                    ) : (
                        <>{fallbackDefault}</>
                    )
                }
                return <DefaultError error={error} errorInfo={errorInfo} />
            }}
        >
            {children}
        </OriginalErrorBoundary>
    )
}
